import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { FrontFlowLogo } from '@/components/brand/FrontFlowLogo'
import { LogoMark } from '@/components/brand/LogoMark'
import { MARK_NAVY, MARK_WHITE } from '@/components/brand/markPath'
import { Button } from '@/components/ui/Button'

// Hosted with the app (was on the old Webflow site's CDN).
const BG_IMAGE = '/images/login-warehouse.jpg'

function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <g fill="none" fillRule="evenodd">
        <path fill="#F25022" d="M0 0h9.474v9.474H0z" />
        <path fill="#00A4EF" d="M0 10.526h9.474V20H0z" />
        <path fill="#7FBA00" d="M10.526 0H20v9.474h-9.474z" />
        <path fill="#FFB900" d="M10.526 10.526H20V20h-9.474z" />
      </g>
    </svg>
  )
}

export default function Auth() {
  const { signIn, isAuthenticated, isLoading, authError } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  const displayError = authError || error

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/')
    }
  }, [isAuthenticated, isLoading, navigate])

  const handleSignIn = async () => {
    setError(null)
    try {
      await signIn()
    } catch (err) {
      console.error('Sign in failed:', err)
      setError('Sign-in failed. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <div className="text-center">
          <LogoMark
            size={48}
            color={MARK_WHITE}
            background={MARK_NAVY}
            className="mx-auto mb-4 animate-spin [animation-duration:1.6s]"
          />
          <p className="text-white/70">Signing you in...</p>
        </div>
      </div>
    )
  }

  // Like the website's product heroes: navy, the photo faded behind a navy vignette,
  // the white logo above a light card.
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-gray-900 px-4">
      <img
        src={BG_IMAGE}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover object-[50%_55%] opacity-30 grayscale-[40%]"
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 60% at 50% 55%, #0B2040 0%, rgba(11,32,64,0.7) 55%, rgba(11,32,64,0.2) 100%),' +
            'linear-gradient(180deg, rgba(11,32,64,0.55) 0%, transparent 35%, rgba(11,32,64,0.6) 80%, #0B2040 100%)',
        }}
      />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        <FrontFlowLogo onDark className="mb-8" />

        <div className="w-full rounded-panel border border-canvas-border bg-surface px-8 py-10 shadow-2xl">
          <div className="space-y-8">
            <div className="space-y-2 text-center">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">Welcome to FrontFlow</h1>
              <p className="text-gray-600">Sign in to continue</p>
            </div>

            <div className="space-y-4">
              {displayError && (
                <p className="text-center text-sm text-destructive" role="alert">
                  {displayError}
                </p>
              )}
              {/* Microsoft's sign-in button guidelines: white, gray border, their logo. */}
              <Button
                onClick={handleSignIn}
                className="h-12 w-full border border-[#8c8c8c] bg-white text-base text-[#1f1f1f] hover:bg-[#f5f5f5] hover:text-[#1f1f1f] active:bg-[#ededed]"
                size="lg"
              >
                <MicrosoftLogo className="mr-2" />
                Log in with Microsoft
              </Button>
            </div>

            <p className="text-center text-sm text-gray-600">
              <a href="https://www.frontflow.com/contact" className="underline underline-offset-2 hover:text-gray-900">Contact us</a> to get started with FrontFlow
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
